import { type LDFlagSet, type LDContext, LDProvider } from 'launchdarkly-react-client-sdk'
import { logError } from 'lib/tracking/datadog'
import { createFilteredLDLogger } from 'lib/launchdarkly-sdk/createFilteredLDLogger'
import { getLDApplicationInfo } from 'lib/launchdarkly-sdk/getLDApplicationInfo'
import type { AnonymousLDContext } from 'lib/launchdarkly-sdk/types'
import { useIsSSR } from 'lib/hooks/useIsSSR/useIsSSR'
import { redactUrlSecrets } from 'lib/helpers/redactUrlSecrets/redactUrlSecrets'
import { flags } from './FeatureFlagsProvider.consts'
import { GlobalLDFlagsGetter } from './hooks/useGlobalLDFlagsGetter/useGlobalLDFlagsGetter'

const clientSideID = process.env.LAUNCHDARKLY_ID

// Shared across every mount: on context-less pages (e.g. `/login`) the client initializes
// with no bootstrap and fetches flags over the network, so a transient failure would
// otherwise spam `console.error` (and Datadog RUM) on each dropped request.
const ldLogger = createFilteredLDLogger()

const ldApplication = getLDApplicationInfo()

// `events.launchdarkly.com` is on browser tracker blocklists (Firefox ETP, uBlock), so the analytics
// flush is dropped at the network layer and the browser prints a CORS error every two seconds (the
// SDK's flush interval) that no SDK logger can intercept. Silenced outside a production build only,
// since LaunchDarkly derives flag insights and experiment metrics from these events; the local
// tradeoff is that a dev session no longer registers in LaunchDarkly's Contexts list. Flag delivery
// runs on separate hosts and is unaffected.
const sendEvents = process.env.NODE_ENV === 'production'

// Without a bound, a flag request that neither responds nor errors suspends initialization for the
// lifetime of the page. 5 is the SDK's ceiling — above it, the bound costs a console warning.
// Expiry is not terminal: the SDK logs through its `error` channel, keeps retrying, and applies the
// flags once they land. Only bootstrap-less mounts can reach it — a bootstrap object marks the
// client initialized without a fetch.
const LD_INIT_TIMEOUT_SECONDS = 5

// Keyed, not keyless: a keyless context makes the SDK mint a random key per browser, so every
// bot or crawler session on a context-less page (e.g. `/login`) counts as a new client-side MAU.
const ANONYMOUS_CONTEXT: AnonymousLDContext = { kind: 'user', key: 'anonymous', anonymous: true }

export interface FeatureFlagsProviderProps {
  context?: LDContext | undefined
  bootstrap?: LDFlagSet | undefined
}

export const FeatureFlagsProvider: Settle.FC<FeatureFlagsProviderProps> = ({ children, context, bootstrap }) => {
  const isHydrated = !useIsSSR()

  if (!clientSideID) {
    logError(new Error('LAUNCHDARKLY_ID is not set'))
    return children
  }

  // Withhold the context until after hydration. The LD provider fires an
  // unconditional setState the moment it initializes; landing inside the page's
  // hydration window switches the Next.js page Suspense boundary to client
  // rendering (React #421). `deferInitialization` skips init while no context is
  // present, so supplying it post-hydration moves that update out of the window.
  return (
    <LDProvider
      flags={flags}
      context={isHydrated ? context ?? ANONYMOUS_CONTEXT : undefined}
      clientSideID={clientSideID}
      options={{
        bootstrap,
        logger: ldLogger,
        application: ldApplication,
        sendEvents,
        eventUrlTransformer: (url) => redactUrlSecrets({ url }),
        // Diagnostics post to that same blocklisted host but carry only SDK and platform telemetry
        // LaunchDarkly uses for its own development — nothing we consume. `sendEvents` already
        // suppresses them outside production; this turns them off there too, matching the server SDK.
        diagnosticOptOut: true,
      }}
      deferInitialization={true}
      timeout={LD_INIT_TIMEOUT_SECONDS}
    >
      {children}
      <GlobalLDFlagsGetter />
    </LDProvider>
  )
}
